import React from 'react';
import {
  Box,
  Paper,
  Typography,
  Alert,
  AlertTitle,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Chip,
  Button,
  useTheme,
  alpha,
  Fade,
  Divider
} from '@mui/material';
import {
  Warning as WarningIcon,
  Error as ErrorIcon,
  Info as InfoIcon,
  CheckCircle as CheckCircleIcon,
  Inventory as InventoryIcon,
  Build as BuildIcon,
  Schedule as ScheduleIcon,
  TrendingDown as TrendingDownIcon,
  Notifications as NotificationsIcon
} from '@mui/icons-material';

interface AlertItem {
  id: string;
  type: 'error' | 'warning' | 'info' | 'success';
  title: string;
  message: string;
  count?: number;
  action?: {
    label: string;
    onClick: () => void;
  };
  icon?: React.ReactNode;
}

interface DashboardAlertsProps {
  alerts?: AlertItem[];
  lowStockCount?: number;
  overdueMaintenanceCount?: number;
  pendingOrdersCount?: number;
}

const DashboardAlerts: React.FC<DashboardAlertsProps> = ({
  alerts = [],
  lowStockCount = 0,
  overdueMaintenanceCount = 0,
  pendingOrdersCount = 0
}) => {
  const theme = useTheme();

  // Generate default alerts based on props
  const defaultAlerts: AlertItem[] = [
    ...(lowStockCount > 0 ? [{
      id: 'low-stock',
      type: 'warning' as const,
      title: 'تحذير مخزون منخفض',
      message: `يوجد ${lowStockCount} منتج بمخزون منخفض يحتاج إعادة تموين`,
      count: lowStockCount,
      icon: <InventoryIcon />,
      action: {
        label: 'عرض المنتجات',
        onClick: () => window.location.href = '/admin/inventory'
      }
    }] : []),
    
    ...(overdueMaintenanceCount > 0 ? [{
      id: 'overdue-maintenance',
      type: 'error' as const,
      title: 'صيانة متأخرة',
      message: `يوجد ${overdueMaintenanceCount} جهاز تجاوز الموعد المحدد للصيانة`,
      count: overdueMaintenanceCount,
      icon: <BuildIcon />,
      action: {
        label: 'عرض الصيانة',
        onClick: () => window.location.href = '/admin/maintenance-records'
      }
    }] : []),
    
    ...(pendingOrdersCount > 0 ? [{
      id: 'pending-orders',
      type: 'info' as const,
      title: 'طلبات في الانتظار',
      message: `يوجد ${pendingOrdersCount} طلب في انتظار المعالجة`,
      count: pendingOrdersCount,
      icon: <ScheduleIcon />,
      action: {
        label: 'عرض الطلبات',
        onClick: () => window.location.href = '/admin/orders'
      }
    }] : [])
  ];

  const allAlerts = [...defaultAlerts, ...alerts];

  const getAlertIcon = (type: string) => {
    switch (type) {
      case 'error':
        return <ErrorIcon />;
      case 'warning':
        return <WarningIcon />;
      case 'info':
        return <InfoIcon />;
      case 'success':
        return <CheckCircleIcon />;
      default:
        return <InfoIcon />;
    }
  };

  const getAlertColor = (type: string) => {
    switch (type) {
      case 'error':
        return theme.palette.error.main;
      case 'warning':
        return theme.palette.warning.main;
      case 'info':
        return theme.palette.info.main;
      case 'success':
        return theme.palette.success.main;
      default:
        return theme.palette.info.main;
    }
  };

  if (allAlerts.length === 0) {
    return (
      <Fade in timeout={1000}>
        <Paper
          sx={{
            p: 3,
            borderRadius: 3,
            textAlign: 'center',
            border: `1px solid ${alpha(theme.palette.success.main, 0.2)}`,
            background: `linear-gradient(135deg, ${alpha(theme.palette.success.main, 0.05)} 0%, ${alpha(theme.palette.success.main, 0.02)} 100%)`
          }}
        >
          <CheckCircleIcon 
            sx={{ 
              fontSize: 48, 
              color: theme.palette.success.main, 
              mb: 2 
            }} 
          />
          <Typography variant="h6" color="success.main" gutterBottom>
            كل شيء يعمل بشكل مثالي! 🎉
          </Typography>
          <Typography variant="body2" color="text.secondary">
            لا توجد تنبيهات أو مشاكل تحتاج انتباهك حالياً
          </Typography>
        </Paper>
      </Fade>
    );
  }

  return (
    <Fade in timeout={1000}>
      <Paper
        sx={{
          borderRadius: 3,
          overflow: 'hidden',
          border: `1px solid ${alpha(theme.palette.divider, 0.1)}`
        }}
      >
        {/* Header */}
        <Box
          sx={{
            p: 3,
            pb: 2,
            background: `linear-gradient(135deg, ${alpha(theme.palette.warning.main, 0.05)} 0%, ${alpha(theme.palette.warning.main, 0.02)} 100%)`,
            borderBottom: `1px solid ${alpha(theme.palette.divider, 0.1)}`
          }}
        >
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
            <NotificationsIcon color="warning" />
            <Typography variant="h6" fontWeight="bold">
              التنبيهات والإشعارات
            </Typography>
            <Chip
              label={allAlerts.length}
              color="warning"
              size="small"
              sx={{ fontWeight: 'bold' }}
            />
          </Box>
        </Box>

        {/* Alerts List */}
        <List sx={{ p: 0 }}>
          {allAlerts.map((alert, index) => {
            const alertColor = getAlertColor(alert.type);
            
            return (
              <React.Fragment key={alert.id}>
                <ListItem
                  sx={{
                    py: 2,
                    px: 3,
                    '&:hover': {
                      bgcolor: alpha(alertColor, 0.05)
                    }
                  }}
                >
                  <ListItemIcon>
                    <Box
                      sx={{
                        width: 40,
                        height: 40,
                        borderRadius: 2,
                        bgcolor: alpha(alertColor, 0.1),
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        color: alertColor
                      }}
                    >
                      {alert.icon || getAlertIcon(alert.type)}
                    </Box>
                  </ListItemIcon>
                  
                  <ListItemText
                    primary={
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5 }}>
                        <Typography variant="subtitle1" fontWeight={600}>
                          {alert.title}
                        </Typography>
                        {alert.count && (
                          <Chip
                            label={alert.count}
                            color={alert.type}
                            size="small"
                            variant="outlined"
                          />
                        )}
                      </Box>
                    }
                    secondary={
                      <Typography variant="body2" color="text.secondary">
                        {alert.message}
                      </Typography>
                    }
                  />
                  
                  {alert.action && (
                    <Button
                      size="small"
                      variant="outlined"
                      color={alert.type}
                      onClick={alert.action.onClick}
                      sx={{ borderRadius: 2 }}
                    >
                      {alert.action.label}
                    </Button>
                  )}
                </ListItem>
                
                {index < allAlerts.length - 1 && (
                  <Divider variant="inset" component="li" sx={{ ml: 8 }} />
                )}
              </React.Fragment>
            );
          })}
        </List>

        {/* Footer */}
        <Box
          sx={{
            p: 2,
            textAlign: 'center',
            borderTop: `1px solid ${alpha(theme.palette.divider, 0.1)}`,
            bgcolor: alpha(theme.palette.background.default, 0.5)
          }}
        >
          <Typography variant="caption" color="text.secondary">
            آخر تحديث: {new Date().toLocaleString('ar-EG')}
          </Typography>
        </Box>
      </Paper>
    </Fade>
  );
};

export default DashboardAlerts;
